import { ref } from 'vue'
import axios from 'axios'
import camelcaseKeys from 'camelcase-keys'

export type CouponDuration = 'once' | 'forever' | 'repeating'

export type AppliedCoupon = {
  code: string
  percentOff: number
  duration: CouponDuration
  durationInMonths: number | null
  /** Server-computed discounted amounts (dollars) per plan id, from coupon_check. */
  plans: Record<number, { amount: number; amountPerMonth: number; discount: number }>
}

type DiscountedPlanResponse = {
  id: number
  amount: number
  amountPerMonth: { total: number; subtotal: number; tax: number }
  discount: number
}

export const COUPON_ERROR_MESSAGE = "That code didn't work. Check the spelling and try again."

// The wording a discount carries next to its re-priced total. Stripe applies
// `once` coupons to the first invoice only, `forever` to every renewal, and
// `repeating` to invoices within the first N months. Also used for the
// limited-time offer, which carries the same duration fields.
export const couponWording = (
  coupon: Pick<AppliedCoupon, 'duration' | 'durationInMonths'>,
  billingInterval: string,
): string => {
  if (coupon.duration === 'once') return billingInterval === 'annually' ? 'your first year' : 'your first quarter'
  if (coupon.duration === 'repeating' && coupon.durationInMonths) return `your first ${coupon.durationInMonths} months`
  return ''
}

// "Have a coupon?" flow: advisory check against GET /stripe/coupon_check.
// The checkout POST re-validates server-side (Subscription::Coupon::Resolve),
// so this state is display only. A code is treated as working only when it
// discounts at least one of the plans the caller renders.
export const useCouponCode = (renderedPlanIds: () => number[]) => {
  const open = ref(false)
  const input = ref('')
  const error = ref('')
  const checking = ref(false)
  const applied = ref<AppliedCoupon | null>(null)

  const apply = async () => {
    const code = input.value.trim()
    if (!code || checking.value) return
    checking.value = true
    error.value = ''
    try {
      const { data } = await axios.get('/stripe/coupon_check', { params: { coupon_code: code } })
      const plans = camelcaseKeys(data.plans ?? [], { deep: true }) as DiscountedPlanResponse[]
      const discounted = plans.filter(
        (plan) => plan.discount > 0 && renderedPlanIds().includes(plan.id),
      )
      if (!data.is_valid || !data.coupon || discounted.length === 0) {
        error.value = COUPON_ERROR_MESSAGE
        return
      }
      const meta = camelcaseKeys(data.coupon)
      applied.value = {
        code: code.toUpperCase(),
        percentOff: meta.percentOff,
        duration: meta.duration,
        durationInMonths: meta.durationInMonths,
        plans: Object.fromEntries(
          discounted.map((plan) => [
            plan.id,
            { amount: plan.amount, amountPerMonth: plan.amountPerMonth.total, discount: plan.discount },
          ]),
        ),
      }
    } catch {
      error.value = COUPON_ERROR_MESSAGE
    } finally {
      checking.value = false
    }
  }

  const remove = () => {
    applied.value = null
    input.value = ''
    error.value = ''
    open.value = false
  }

  return { open, input, error, checking, applied, apply, remove }
}
